import { Box, Button, Container, Divider, Flex, HStack, SimpleGrid, Spacer, Text, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import SearchString from "search-string";
import QuestionCard from "./components/QuestionCard";
import QuestionCardTable from "./components/QuestionCardTable";
import FilterByComponent, { TEXT_KEYWORD } from "./components/tools/FilterByComponent";
import GroupByComponent from "./components/tools/GroupByComponent";
import ViewTypeComponent from "./components/tools/ViewTypeComponent";
import { H2Dashboard } from "components/text/headings";
import GLOBAL from "context/global";
import BulkActionsList from "./components/tools/BulkActionsList";
import { numberForReference } from "./helper";
import { isExcludedFn } from "api/issb/Question";
import { FiChevronDown, FiChevronsDown, FiChevronUp, FiGrid } from "react-icons/fi";
import { colors } from "theme/foundations/colors";

/**
 * Question List page
 * Design: https://www.figma.com/design/uDql1qJTNBIvGnFDsOpWV4/ESG360-AIR?node-id=1426-76368&p=f&t=2vJ4zWPNETnMuKqh-0
 */
const DataInputPanel = ({ fullQuestionList, defaultGroupBy = "questionGroup", bulkActions }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "issb.questionGroup.headings",
  });

  const [viewType, setViewType] = useState("List");
  const [groupBy, setGroupBy] = useState(defaultGroupBy);
  // store filter-by as a URL parameter `q` -- This supports link-to-filtered-view (eg from dashboard), plus browser tabs
  const [searchParams, setSearchParams] = useSearchParams();
  const filterBy = searchParams.get("q") || "";
  const setFilterBy = (value) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("q", value);
    setSearchParams(newParams);
  };

  // apply filters
  // HACK: if the query parameter says, then do show excluded -- otherwise they are hidden by default
  const showExcluded = filterBy.toUpperCase().includes("EXCLUDED") || filterBy.toUpperCase().includes("NOT_APPLICABLE");
  const filteredQuestionList = applyFiltersOverList(fullQuestionList, filterBy, showExcluded) || [];

  // sort the questions
  const sortFn = (a, b) => {
    if (a.issbReference) {
      // Note: undefined issbReference shouldn't happen -- but seen in some data (april 2025)
      if (b.issbReference) {
        // try to make a sorting number from the issbReference
        const aNum = numberForReference(a.issbReference);
        const bNum = numberForReference(b.issbReference);
        if (aNum && bNum) {
          return aNum - bNum;
        }
        return a.issbReference.localeCompare(b.issbReference, undefined, { numeric: true });
      }
      // b has no issbReference, so a is first
      return -1;
    }
    if (b.issbReference) {
      // a has no issbReference, so b is first
      return 1;
    }
    if (a.question && b.question) {
      return a.question.localeCompare(b.question, undefined, { numeric: true });
    }
    // paranoia: fallback to id, which is adhoc but stable
    return a.id.localeCompare(b.id, undefined, { numeric: true });
  };
  const sortedFilteredQuestionList = filteredQuestionList?.slice().sort(sortFn);

  // Variable to manage expansion and collapse of all the requirement cards.
  const [expandAll, setExpandAll] = useState(false);
  const toggleExpandAllCard = () => {
    setExpandAll((prev) => !prev);
  };

  // group them
  const filteredQuestionGroups = groupQuestions(sortedFilteredQuestionList, groupBy); // Dan: I think the sorting will carry through to here TODO test that
  const filteredGroupNames = Object.keys(filteredQuestionGroups);

  // HACK speed up the navigation off this (slow) screen by avoiding an extra full render
  const [pathname0] = useState(window.location.pathname);
  const pathname = window.location.pathname;
  if (pathname !== pathname0) {
    return <div>Loading...</div>;
  }

  return (
    <Container maxW="100vw" py={8} p={0} id="data-input-panel">
      <HStack align="start" spacing={4}>
        <Flex my={2} direction="row" gap={4} py={0} width="100%">
          <FilterByComponent
            filterBy={filterBy}
            setFilterBy={setFilterBy}
            fullList={fullQuestionList}
            filteredList={filteredQuestionList}
          />
          <GroupByComponent groupBy={groupBy} setGroupBy={setGroupBy} />
          <ViewTypeComponent viewType={viewType} setViewType={setViewType} />

          {/* ADDED THE EXPAND ALL BUTTON HERE. */}
          {/* ..................................... */}
          <Button
            variant="primary"
            borderRadius="full"
            color={expandAll ? "pureWhite" : "pureBlack"}
            bg={expandAll ? "teal" : "gray.100"}
            fontSize="body.xs"
            rightIcon={
              expandAll ? (
                <FiChevronUp color={colors.limeShade[600]} />
              ) : (
                <FiChevronDown color={colors.limeShade[600]} />
              )
            }
            size="sm"
            onClick={toggleExpandAllCard}
          >
            {expandAll ? "Collapse all" : "Expand all"}
          </Button>
          {/* ADDED THE EXPAND ALL BUTTON HERE. */}

          {filterBy && (
            <Button size="xs" variant="plain" fontSize="body.xxs" alignSelf="center" onClick={() => setFilterBy("")}>
              Clear Filters
            </Button>
          )}
          <Spacer />
          <BulkActionsList filteredQuestionList={filteredQuestionList} filterBy={filterBy} actionList={bulkActions} />
        </Flex>
      </HStack>

      <Text fontSize="18px" fontWeight="700" color="gray.600" mt={2}>
        {filteredQuestionList?.length} {filteredQuestionList?.length === 1 ? "requirement" : "requirements"}
        {filterBy && fullQuestionList && filteredQuestionList?.length !== fullQuestionList.length && (
          <Text as="span" fontWeight="normal" color="limeShade.600"> of {fullQuestionList.length}</Text>
        )}
      </Text>

      <>
        {(viewType === "List" || viewType === "Grid") && (
          <VStack spacing={4}>
            {filteredGroupNames?.map((groupName, index) => (
              <Box key={index} width="100%">
                {(filteredQuestionGroups.length > 1 || groupName !== "Ungrouped") && (
                  <H2Dashboard alignSelf="start" mt={8}>
                    {groupName}
                  </H2Dashboard>
                )}

                {/* Purpose only show if translation have data */}
                {t(groupName, { defaultValue: null }) ? (
                  <Text fontSize="body.s" color="gray.600" fontWeight="normal" mt={2}>
                    <Text as="span" fontWeight="bold" display="inline" fontSize="inherit" color="inherit">
                      Purpose:
                    </Text>{" "}
                    {t(groupName)}
                  </Text>
                ) : null}

                <Divider my={4} borderColor="gray.200" />
                <SimpleGrid columns={{ base: 1, md: viewType === "Grid" ? 2 : 1 }} spacing={4} alignItems="start">
                  {filteredQuestionGroups[groupName].map((item, index) => (
                    <QuestionCard key={index} question={item} showExcluded={showExcluded} expandCard={expandAll} />
                  ))}
                </SimpleGrid>
              </Box>
            ))}
          </VStack>
        )}

        {viewType === "Table" &&
          Object.keys(filteredQuestionGroups)?.map((groupName, index) => {
            const filteredQuestionGroup = filteredQuestionGroups[groupName];
            return (
              <Box key={index}>
                <Text key={index} as="h3" color="teal" fontSize="heading.s" fontWeight="bold" alignSelf="start" mt={8}>
                  {groupName}
                </Text>
                <Divider my={4} borderColor="gray.200" />
                <QuestionCardTable questionList={filteredQuestionGroup} />
              </Box>
            );
          })}
      </>
    </Container>
  );
};

/**
 *
 * @param {Question[]} questions
 * @param {string} filterBy A search query string using "gmail search syntax" of key:value pairs and - (not)
 * @returns {Question[]} filtered list
 */
export function applyFiltersOverList(questions, filterBy, showExcluded) {
  if (!questions) return questions; // loading
  // filter out excluded and not-applicable questions
  if (!showExcluded) {
    questions = questions.filter((question) => !isExcludedFn(question));
  }

  if (!filterBy) return questions;
  const search = SearchString.parse(filterBy);
  const conditions = search.getConditionArray();
  // The search as key:[value, value2] E.g. {owner:["alice@eg.com"]}
  const values4keyword = {};
  for (const condition of conditions) {
    const { keyword, value } = condition;
    values4keyword[keyword] = (values4keyword[keyword] || []).concat(value);
  }
  // HACK: Special case for questionIdsFilter:true
  const questionIdsFilter = values4keyword.questionIdsFilter;
  if (questionIdsFilter?.includes("true")) {
    let questionIds = GLOBAL.questionIdsFilter?.ids;
    if (questionIds) {
      questions = questions.filter((question) => questionIds.includes(question.id));
    } else {
      console.warn("questionIdsFilter:true but no questionIdsFilter in global");
      // TODO handle page reload by removing the filter
    }
  }
  delete values4keyword.questionIdsFilter; // Not wanted for the filter below. This delete has no side-effects beyond that.

  const keywords = Object.keys(values4keyword);

  const qFilter = (question) => {
    return keywords.every((keyword) => {
      const fValues = values4keyword[keyword];
      if (keyword === TEXT_KEYWORD) {
        const questionText = JSON.stringify(question).toLowerCase();
        const words = fValues[0].toLowerCase().split(" "); // assumes: words returned as one value, space-separated
        return words.every((word) => questionText.includes(word));
      }
      const qValues = question[keyword];
      return Array.isArray(qValues) ? qValues.some((item) => fValues.includes(item)) : fValues.includes(qValues);
    });
  }; // ./qFilter()

  return questions.filter(qFilter);
} // ./applyFiltersOverList()

function groupQuestions(questions, groupBy) {
  return questions.reduce((acc, question) => {
    const key = question[groupBy] || "Ungrouped";
    if (!acc[key]) acc[key] = [];
    acc[key].push(question);
    return acc;
  }, {});
}

export default DataInputPanel;
